import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar } from "@/components/User/Avatar";
import { MentionParser } from "@/components/Home/MentionParser";
import { formatDistanceToNow } from "date-fns";
import { safeSelect, safeInsert } from "@/utils/supabase/queries";
import { toDatabaseValue } from "@/utils/supabase/type-guards";
import { useMentionNotifications } from "@/hooks/use-mention-notifications";
import { Reply, X, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

type Comment = {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  parent_id: string | null;
  profiles: {
    username: string;
    avatar_id: number | null;
  };
  replies?: Comment[];
};

type PromptCommentsProps = {
  promptId: string;
  onNewComment: () => void;
};

export function PromptComments({ promptId, onNewComment }: PromptCommentsProps) {
  const { user, userProfile } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const { notifyMentionedUsers } = useMentionNotifications();

  const fetchComments = useCallback(async () => {
    try {
      const data = await safeSelect<any>(
        supabase
          .from("prompt_comments")
          .select(`
            id,
            content,
            created_at,
            user_id,
            parent_id,
            profiles (
              username,
              avatar_id
            )
          `)
          .eq("prompt_id", toDatabaseValue(promptId))
          .order("created_at", { ascending: true }),
        "Failed to load comments"
      );

      if (data && Array.isArray(data)) {
        // Transform and thread comments
        const transformedData = data
          .filter((comment: any) => comment?.id && comment?.profiles)
          .map((comment: any) => ({
            id: comment.id,
            content: comment.content,
            created_at: comment.created_at,
            user_id: comment.user_id,
            parent_id: comment.parent_id,
            profiles: Array.isArray(comment.profiles)
              ? comment.profiles[0]
              : comment.profiles,
            replies: [] as Comment[],
          })) as Comment[];

        // Build threaded structure
        const commentMap = new Map<string, Comment>();
        const rootComments: Comment[] = [];

        transformedData.forEach((comment) => {
          commentMap.set(comment.id, comment);
        });

        transformedData.forEach((comment) => {
          if (comment.parent_id && commentMap.has(comment.parent_id)) {
            const parent = commentMap.get(comment.parent_id)!;
            parent.replies = parent.replies || [];
            parent.replies.push(comment);
          } else if (!comment.parent_id) {
            rootComments.push(comment);
          }
        });

        setComments(rootComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [promptId]);

  // Initial fetch
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel(`prompt-comments-${promptId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "prompt_comments",
          filter: `prompt_id=eq.${promptId}`,
        },
        (payload) => {
          console.log("Comment change:", payload);
          // Refetch to get proper threaded structure
          fetchComments();
          
          if (payload.eventType === "INSERT") {
            // Only increment if it's a new comment from another user
            const newCommentUserId = (payload.new as any)?.user_id;
            if (newCommentUserId && newCommentUserId !== user?.id) {
              onNewComment();
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [promptId, fetchComments, user?.id, onNewComment]);

  const handleSubmitComment = async () => {
    if (!user || !newComment.trim()) {
      if (!user) {
        toast.error("Please sign in to comment");
      }
      return;
    }

    setSubmitting(true);
    try {
      const commentPayload: any = {
        prompt_id: promptId,
        user_id: user.id,
        content: newComment.trim(),
      };

      if (replyingTo) {
        commentPayload.parent_id = replyingTo.id;
      }

      const commentData = await safeInsert(
        "prompt_comments",
        commentPayload,
        "Failed to add comment"
      );

      if (commentData) {
        // Notify mentioned users
        if (userProfile?.username) {
          await notifyMentionedUsers(
            newComment,
            user.id,
            userProfile.username,
            promptId
          );
        }

        setNewComment("");
        setReplyingTo(null);
        onNewComment();
        toast.success(replyingTo ? "Reply added!" : "Comment added!");
        
        // Auto-expand replies for the parent comment
        if (replyingTo) {
          setExpandedReplies((prev) => new Set([...prev, replyingTo.id]));
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleReplies = (commentId: string) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const handleReply = (comment: Comment) => {
    setReplyingTo(comment);
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setNewComment("");
  };

  const CommentItem = ({ comment, isReply = false }: { comment: Comment; isReply?: boolean }) => {
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isExpanded = expandedReplies.has(comment.id);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn("space-y-2", isReply && "ml-8 pl-3 border-l border-white/10")}
      >
        <div className="flex gap-3">
          <Avatar
            userId={comment.user_id}
            avatarId={comment.profiles?.avatar_id}
            size="sm"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-white/90">
                @{comment.profiles?.username || "Unknown"}
              </span>
              <span className="text-xs text-white/40">
                {formatDistanceToNow(new Date(comment.created_at), {
                  addSuffix: true,
                })}
              </span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap mt-1">
              <MentionParser text={comment.content} />
            </p>

            {/* Reply button */}
            {user && !isReply && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReply(comment)}
                className="h-6 px-2 text-xs text-white/40 hover:text-brand-cyan mt-1"
              >
                <Reply className="h-3 w-3 mr-1" />
                Reply
              </Button>
            )}

            {/* Show replies toggle */}
            {hasReplies && !isReply && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleReplies(comment.id)}
                className="h-6 px-2 text-xs text-brand-cyan/70 hover:text-brand-cyan ml-2"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Hide {comment.replies!.length} {comment.replies!.length === 1 ? "reply" : "replies"}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    Show {comment.replies!.length} {comment.replies!.length === 1 ? "reply" : "replies"}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Nested replies */}
        <AnimatePresence>
          {hasReplies && isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden space-y-3 mt-2"
            >
              {comment.replies!.map((reply) => (
                <CommentItem key={reply.id} comment={reply} isReply />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-4 text-white/50 text-sm">
        Loading comments...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Comment input */}
      {user && (
        <div className="space-y-2">
          {/* Reply indicator */}
          {replyingTo && (
            <div className="flex items-center gap-2 text-xs text-brand-cyan bg-brand-cyan/10 px-3 py-2 rounded-lg">
              <Reply className="h-3 w-3" />
              <span>Replying to @{replyingTo.profiles?.username}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelReply}
                className="h-5 w-5 p-0 ml-auto text-white/50 hover:text-white"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          <Textarea
            placeholder={replyingTo ? `Reply to @${replyingTo.profiles?.username}...` : "Add a comment..."}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={2}
            className="resize-none bg-white/5 border-white/10 text-white placeholder:text-white/40"
          />
          <div className="flex gap-2">
            <Button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || submitting}
              size="sm"
              className="bg-brand-cyan hover:bg-brand-cyan/80 text-black"
            >
              {submitting ? "Posting..." : replyingTo ? "Reply" : "Comment"}
            </Button>
            {replyingTo && (
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelReply}
                className="text-white/50"
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-4">
        {comments.map((comment) => (
          <CommentItem key={comment.id} comment={comment} />
        ))}
        {comments.length === 0 && (
          <p className="text-white/40 text-sm text-center py-4">
            No comments yet. Be the first to comment!
          </p>
        )}
      </div>
    </div>
  );
}
